import type { ReactNode } from "react";

import {
  DEFAULT_APPEARANCE_CONTRAST,
  MAX_APPEARANCE_CONTRAST,
  MIN_APPEARANCE_CONTRAST,
} from "@cmmd-forge/contracts";
import { DEFAULT_THEME_ID, type ThemeAppearance } from "@cmmd-forge/contracts/theme";

import { useCustomThemes } from "../../hooks/useCustomThemes";
import type { ThemeHalves } from "../../themeLibrary";

import { useSettings, useUpdateSettings } from "../../hooks/useSettings";
import { SettingResetButton, SettingsBlock, SettingsRow } from "./settingsLayout";
import { searchableSetting } from "./settingsSearch";
import { ThemeCustomSection, ThemeHalvesRow } from "./ThemeCustomThemes";
import { ThemePicker } from "./ThemePicker";

/**
 * The theme half of the Appearance panel: the palette picker, the
 * per-appearance mix, the installed-theme management, and the contrast
 * slider. Extracted so the panel stays a composition of sections rather than
 * a 300-plus-line file. Props-driven on purpose: the panel's single
 * `useTheme` instance stays the one applier in the tree, so this section
 * must not mount its own.
 *
 * Row titles come from the settings-search catalog through the
 * `searchableSetting` spread, never from a literal here.
 *
 * @module
 */

/**
 * Its own component for the same reason the panel's other slider rows are:
 * the panel stays a list of rows rather than a function that also knows which
 * setting a slider reads. 100 is the shipped look; the measurable effect
 * lives in the `--contrast-*` axis, and `appearanceWiringGuard.browser.tsx`
 * proves the derived roles actually move.
 */
export function ContrastRow(): ReactNode {
  const appearanceContrast = useSettings((settings) => settings.appearanceContrast);
  const { updateSettings } = useUpdateSettings();

  return (
    <SettingsRow
      {...searchableSetting("appearance-contrast")}
      description="Raises or softens text and border contrast across the interface, derived from the colours each surface already uses."
      resetAction={
        appearanceContrast !== DEFAULT_APPEARANCE_CONTRAST ? (
          <SettingResetButton
            label="contrast"
            onClick={() => updateSettings({ appearanceContrast: DEFAULT_APPEARANCE_CONTRAST })}
          />
        ) : null
      }
      control={
        <div className="flex w-full items-center gap-3 sm:w-52">
          <output
            className="min-w-12 rounded-md bg-muted px-2 py-1 text-center font-medium font-mono text-foreground text-xs tabular-nums"
            htmlFor="appearance-contrast"
          >
            {appearanceContrast}%
          </output>
          <input
            aria-label="Interface contrast"
            className="min-h-touch min-w-0 flex-1 md:min-h-0"
            id="appearance-contrast"
            max={MAX_APPEARANCE_CONTRAST}
            min={MIN_APPEARANCE_CONTRAST}
            onChange={(event) => {
              const next = Number(event.currentTarget.value);
              if (Number.isInteger(next)) updateSettings({ appearanceContrast: next });
            }}
            step={5}
            type="range"
            value={appearanceContrast}
          />
        </div>
      }
    />
  );
}

/**
 * The palette picker block, the per-appearance mix row, and the installed
 * custom-theme management, as one section. All selection state arrives as
 * props from the panel's `useTheme`, so removal handling can see both the
 * base preference and the mix without a second hook instance.
 */
export function AppearanceThemeSection(props: {
  readonly themeId: string;
  readonly resolvedTheme: ThemeAppearance;
  readonly themeHalves: ThemeHalves | null;
  readonly onThemeIdChange: (id: string) => void;
  readonly onSetHalf: (appearance: ThemeAppearance, themeId: string | null) => void;
  readonly onClearHalves: () => void;
}): ReactNode {
  const { themeId, resolvedTheme, themeHalves } = props;
  // A read-only subscription, not state: the library module owns the data,
  // and this does not create a second theme applier.
  const customThemes = useCustomThemes();

  /**
   * Removing the theme that is currently selected, or that owns a half, must
   * hand the appearance back to something that exists: the default or the
   * base preference. The mix prunes unknown ids on the next read anyway; this
   * keeps the live state honest immediately.
   */
  const handleThemeRemoved = (removedId: string) => {
    if (themeId === removedId) {
      props.onThemeIdChange(DEFAULT_THEME_ID);
    }
    if (themeHalves?.light === removedId) {
      props.onSetHalf("light", null);
    }
    if (themeHalves?.dark === removedId) {
      props.onSetHalf("dark", null);
    }
  };

  return (
    <>
      <SettingsBlock
        {...searchableSetting("appearance-theme")}
        description="Recolours the app. The colour scheme above still decides light or dark; a theme only changes which colours that scheme is drawn in."
        resetAction={
          themeId !== DEFAULT_THEME_ID ? (
            <SettingResetButton
              label="theme"
              onClick={() => props.onThemeIdChange(DEFAULT_THEME_ID)}
            />
          ) : null
        }
      >
        <ThemePicker
          appearance={resolvedTheme}
          customThemes={customThemes}
          themeId={themeId}
          onThemeIdChange={props.onThemeIdChange}
        />
      </SettingsBlock>
      <SettingsRow
        {...searchableSetting("appearance-theme-halves")}
        description="Pin a different theme to each appearance. An appearance without a pick uses the theme above."
        resetAction={
          themeHalves !== null ? (
            <SettingResetButton
              label="per-appearance themes"
              onClick={() => props.onClearHalves()}
            />
          ) : null
        }
        control={
          <ThemeHalvesRow
            customThemes={customThemes}
            halves={themeHalves}
            onSetHalf={props.onSetHalf}
          />
        }
      />
      <ThemeCustomSection
        resolvedTheme={resolvedTheme}
        themeId={themeId}
        themeHalves={themeHalves}
        onThemeIdChange={props.onThemeIdChange}
        onThemeRemoved={handleThemeRemoved}
      />
    </>
  );
}
